import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
import { LongArrowUpRegularGlyph } from '@acast-tech/decibel-glyphs';
import T from 'prop-types';
import { forwardRef, useMemo } from 'react';
import styled from 'styled-components';
import { createShouldForwardProp } from '../../helpers/createShouldForwardProp';
import { warnOnce } from '../../helpers/warnOnce';
import { useId } from '../../hooks/useId';
import { black, black16, black48, black64, blackAlpha2, blackAlpha8, purpleAlpha16, purpleAlpha8, white } from '../../styles/colors';
import { resetBrowserOutline } from '../../styles/mixins';
import { compose, createPropTypes, layout, padding, system } from '../../styles/system';
import { body2, buttonLabel } from '../../styles/typography';
import { unit } from '../../styles/unit';
import { HStack } from '../HStack/HStack';
import { Icon } from '../Icon/Icon';
import { Label } from '../Label/Label';
import { Pagination } from '../Pagination/Pagination';
import { Select, SelectOption } from '../Select/Select';
import { Text } from '../Text/Text';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
const TableBase = /*#__PURE__*/styled.table.withConfig({
  displayName: "TableBase",
  componentId: "a1dd92-0"
})(["width:100%;background-color:", ";table-layout:", ";"], white, props => props.fixedLayout ? 'fixed' : null);

function Table(props) {
  // eslint-disable-next-line react/prop-types
  if (process.env.NODE_ENV !== "production" && !props['aria-label'] && !props['aria-labelledby']) {
    warnOnce('For accessibility reasons please add a "aria-label" or "aria-labelledby" prop on the <Table />');
  }

  return /*#__PURE__*/_jsx(TableBase, Object.assign({}, props));
}

Table.propTypes = {
  fixedLayout: T.bool
}; // TableContainer

const TableContainer = /*#__PURE__*/styled.div.withConfig({
  displayName: "TableContainer",
  componentId: "a1dd92-1"
})(["width:100%;overflow-x:auto;"]); // TableCell

const tableCellTextWrap = system({
  textWrap: value => !value ? {
    whiteSpace: 'nowrap'
  } : null
});
const tableCellAlign = system({
  align: {
    property: 'textAlign'
  }
});
const tableCellStyleProps = compose(padding, layout.height, tableCellAlign, tableCellTextWrap).withDefaults({
  py: 4,
  px: 4,
  height: 18,
  align: 'left',
  textWrap: false
});
const TableCell = /*#__PURE__*/styled.td.withConfig({
  shouldForwardProp: createShouldForwardProp(tableCellStyleProps.propNames)
}).withConfig({
  displayName: "TableCell",
  componentId: "a1dd92-2"
})(["", " ", ";border-bottom:1px solid ", ";vertical-align:middle;"], body2, tableCellStyleProps, black16);
TableCell.propTypes = Object.assign({}, createPropTypes(tableCellStyleProps.propNames), {
  textWrap: T.bool
}); // TableHeadCell

const tableHeadCellAlign = system({
  align: value => ({
    textAlign: value,
    flexDirection: value === 'right' ? 'row-reverse' : null
  })
});
const tableHeadCellStyleProps = compose(padding, layout.height, layout.width, layout.minWidth, layout.maxWidth, tableHeadCellAlign).withDefaults({
  align: 'left',
  height: 10,
  py: 0,
  px: 4
});
const TableHeadCell = /*#__PURE__*/styled.th.withConfig({
  shouldForwardProp: createShouldForwardProp(tableHeadCellStyleProps.propNames)
}).withConfig({
  displayName: "TableHeadCell",
  componentId: "a1dd92-3"
})(["", " ", " border-bottom:1px solid ", ";color:", ";white-space:nowrap;vertical-align:middle;"], buttonLabel, tableHeadCellStyleProps, black16, black64);
TableHeadCell.propTypes = createPropTypes(tableHeadCellStyleProps.propNames); // TableRow

function tableRowStyle(props) {
  if (props.selected) {
    return {
      backgroundColor: purpleAlpha8,
      '&:hover': {
        backgroundColor: purpleAlpha16
      },
      '&:active': {
        backgroundColor: blackAlpha8
      }
    };
  }

  if (props.onClick) {
    return {
      '&:hover': {
        backgroundColor: blackAlpha2
      },
      '&:active': {
        backgroundColor: blackAlpha8
      }
    };
  }

  return null;
}

const TableRow = /*#__PURE__*/styled.tr.withConfig({
  displayName: "TableRow",
  componentId: "a1dd92-4"
})(["", " &:last-child > ", "{border-bottom:none;}"], tableRowStyle, TableCell);
TableRow.propTypes = {
  onClick: T.func,
  selected: T.bool
}; // TableSortLabel

const TableSortIcon = /*#__PURE__*/styled(Icon).withConfig({
  displayName: "TableSortIcon",
  componentId: "a1dd92-5"
})(["margin-left:", ";margin-right:", ";opacity:0;transition-property:opacity,transform;transition-duration:inherit;transition-timing-function:inherit;*:hover > &,.focus-visible > &{opacity:0.6;}[aria-sort] &{opacity:1;}", ""], unit(1), unit(1), props => props.sortDirection ? {
  transform: props.sortDirection === 'desc' ? 'rotate(-180deg)' : null
} : {
  '[aria-sort="descending"] &': {
    transform: 'rotate(-180deg)'
  }
});
const TableSortLabelBase = /*#__PURE__*/styled.span.withConfig({
  displayName: "TableSortLabelBase",
  componentId: "a1dd92-6"
})(["", " display:inline-flex;align-items:center;justify-content:flex-start;flex-direction:inherit;cursor:pointer;user-select:none;transition-property:color;transition-duration:100ms;transition-timing-function:ease-out;[aria-sort] &,&.focus-visible{color:", ";}"], resetBrowserOutline, black);
const TableSortLabel = /*#__PURE__*/forwardRef(function TableSortLabel(_ref, ref) {
  let {
    onClick,
    direction,
    children
  } = _ref,
      restProps = _objectWithoutPropertiesLoose(_ref, ["onClick", "direction", "children"]);

  return /*#__PURE__*/_jsxs(TableSortLabelBase, Object.assign({
    ref: ref,
    tabIndex: 0,
    role: "button",
    onClick: onClick
  }, restProps, {
    children: [children, /*#__PURE__*/_jsx(TableSortIcon, {
      size: "xxsmall",
      glyph: LongArrowUpRegularGlyph,
      sortDirection: direction
    })]
  }));
});
TableSortLabel.propTypes = {
  onClick: T.func,
  children: T.node,
  direction: T.oneOf(['asc', 'desc'])
}; // TablePagination

const TablePaginationContainer = /*#__PURE__*/styled.div.withConfig({
  displayName: "TablePaginationContainer",
  componentId: "a1dd92-7"
})(["display:grid;grid-template-columns:1fr 2fr 1fr;align-items:center;margin-top:", ";"], unit(6));
const PAGE_SIZE_OPTIONS = [10, 25, 50];
const TablePagination = /*#__PURE__*/forwardRef(function TablePagination(_ref2, ref) {
  let {
    current,
    onPageChange,
    onPageSizeChange,
    pageSizeOptions = PAGE_SIZE_OPTIONS,
    pageSize,
    paginationProps,
    summary,
    total
  } = _ref2,
      restProps = _objectWithoutPropertiesLoose(_ref2, ["current", "onPageChange", "onPageSizeChange", "pageSizeOptions", "pageSize", "paginationProps", "summary", "total"]);

  const selectId = `${useId('table-select')}`;
  const numberOfPages = Math.max(1, Math.ceil(total / pageSize));
  const summaryString = useMemo(() => {
    const pageStart = (current - 1) * pageSize;
    const pageEnd = Math.min(current * pageSize, total);

    if (summary === false) {
      return null;
    }

    if (typeof summary === 'function') {
      return summary(pageStart + 1, pageEnd, total);
    }

    if (numberOfPages === 1) {
      return `Showing ${total} of ${total}`;
    }

    return `Showing ${pageStart + 1} to ${pageEnd} of ${total}`;
  }, [current, numberOfPages, pageSize, summary, total]);
  return /*#__PURE__*/_jsxs(TablePaginationContainer, Object.assign({
    ref: ref
  }, restProps, {
    children: [/*#__PURE__*/_jsx("div", {
      children: summaryString && /*#__PURE__*/_jsx(Text, {
        variant: "body2",
        mb: 0,
        textColor: black48,
        children: summaryString
      })
    }), /*#__PURE__*/_jsx(Pagination, Object.assign({
      current: current,
      onPageChange: onPageChange,
      total: numberOfPages,
      innerRange: 1,
      outerRange: 1
    }, paginationProps)), /*#__PURE__*/_jsx(HStack, {
      gap: 2,
      alignY: "center",
      alignX: "trailing",
      children: pageSizeOptions && /*#__PURE__*/_jsxs(_Fragment, {
        children: [/*#__PURE__*/_jsx(Label, {
          mb: 0,
          htmlFor: selectId,
          children: "Page size"
        }), /*#__PURE__*/_jsx(Select, {
          size: "small",
          id: selectId,
          stretch: false,
          value: pageSize,
          onChange: event => onPageSizeChange(Number(event.target.value), event),
          children: pageSizeOptions.map(option => {
            const isObject = typeof option === 'object';
            const value = isObject ? option.value : option;
            const label = isObject ? option.label : option;
            return /*#__PURE__*/_jsx(SelectOption, {
              value: value,
              children: label
            }, value);
          })
        })]
      })
    })]
  }));
});
TablePagination.propTypes = {
  current: T.number.isRequired,
  pageSize: T.number.isRequired,
  total: T.number.isRequired,
  onPageChange: T.func.isRequired,
  onPageSizeChange: T.func.isRequired,
  pageSizeOptions: T.arrayOf(T.oneOfType([T.number, T.shape({
    label: T.string,
    value: T.number
  })])),
  paginationProps: T.shape({}),
  summary: T.oneOfType([T.bool, T.func])
};
const TableHead = /*#__PURE__*/forwardRef(function TableHead(props, ref) {
  return /*#__PURE__*/_jsx("thead", Object.assign({
    ref: ref
  }, props));
});
const TableBody = /*#__PURE__*/forwardRef(function TableBody(props, ref) {
  return /*#__PURE__*/_jsx("tbody", Object.assign({
    ref: ref
  }, props));
});
const TableFoot = /*#__PURE__*/forwardRef(function TableFoot(props, ref) {
  return /*#__PURE__*/_jsx("tfoot", Object.assign({
    ref: ref
  }, props));
});
export { Table, TableCell, TableContainer, TableRow, TableSortLabel, TableHeadCell, TableHead, TableBody, TableFoot, TablePagination };